import { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Switch, Tag, message } from "antd";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { locationsState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitGroupDto,
  DataKitItemDto,
  DataKitItemGroupDto,
  DataKitItemPriceDto,
  DataKitPriceOverrideDto,
  DataKitSubgroupDto,
} from "../../../../domain/DataKitDto";
import "./DataKitItemDrawer.scss";

export function blankDataKitItem(): DataKitItemDto {
  return {
    name: "",
    description: "",
    sequence: 0,
    archived: false,
    available: true,
    prices: [{ size: "", price: "", sequence: 0, overrides: [] }],
    tags: [],
    groups: [],
    modifierGroups: [],
    usedOnCount: 0,
  };
}

const entryKey = (entry: DataKitItemGroupDto) => `${entry.groupName} ${entry.subgroupName || ""}`;

interface Props {
  kit: DataKitDto;
  item: DataKitItemDto;
  groups: DataKitGroupDto[];
  subgroups: DataKitSubgroupDto[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
  onStructureChanged: () => void;
}

export function DataKitItemDrawer({
  kit,
  item,
  groups,
  subgroups,
  canWrite,
  onClose,
  onSaved,
  onStructureChanged,
}: Props) {
  const locations = useRecoilValue(locationsState);

  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description);
  const [prices, setPrices] = useState<DataKitItemPriceDto[]>(
    item.prices.length > 0
      ? [...item.prices].sort((a, b) => a.sequence - b.sequence)
      : [{ size: "", price: "", sequence: 0, overrides: [] }]
  );
  const [showSizeLabels, setShowSizeLabels] = useState(
    item.prices.some((p) => (p.size || "").trim() !== "")
  );
  const [extras, setExtras] = useState(item.extras || "");
  const [tags, setTags] = useState<string[]>([...item.tags]);
  const [tagDraft, setTagDraft] = useState("");
  const [available, setAvailable] = useState(item.available);
  const [entries, setEntries] = useState<DataKitItemGroupDto[]>(
    item.groups.map((g) => ({ groupName: g.groupName, subgroupName: g.subgroupName || null }))
  );
  const [draftGroup, setDraftGroup] = useState<string | undefined>();
  const [draftSubgroup, setDraftSubgroup] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newSubgroupOpen, setNewSubgroupOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const isNew = !item.id;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !newGroupOpen && !newSubgroupOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, newGroupOpen, newSubgroupOpen]);

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((l) => {
      if (l.internalId != null) map.set(String(l.internalId), l.name || "");
    });
    return map;
  }, [locations]);

  const subgroupsForDraft = useMemo(
    () => subgroups.filter((s) => s.groupName === draftGroup),
    [subgroups, draftGroup]
  );

  const allOverrides = useMemo(
    () =>
      prices.flatMap((p, priceIndex) =>
        (p.overrides || []).map((o) => ({ priceIndex, size: p.size, override: o }))
      ),
    [prices]
  );

  const updatePrice = (index: number, patch: Partial<DataKitItemPriceDto>) => {
    setPrices((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const addPrice = () => {
    setPrices((current) => [
      ...current,
      { size: "", price: "", sequence: current.length, overrides: [] },
    ]);
  };

  const removePrice = (index: number) => {
    setPrices((current) =>
      current.filter((_, i) => i !== index).map((p, i) => ({ ...p, sequence: i }))
    );
  };

  const updateOverride = (
    priceIndex: number,
    unitId: string,
    patch: Partial<DataKitPriceOverrideDto>
  ) => {
    setPrices((current) =>
      current.map((p, i) =>
        i === priceIndex
          ? {
              ...p,
              overrides: (p.overrides || []).map((o) =>
                o.unitId === unitId ? { ...o, ...patch } : o
              ),
            }
          : p
      )
    );
  };

  const removeOverride = (priceIndex: number, unitId: string) => {
    setPrices((current) =>
      current.map((p, i) =>
        i === priceIndex
          ? { ...p, overrides: (p.overrides || []).filter((o) => o.unitId !== unitId) }
          : p
      )
    );
  };

  const commitTag = () => {
    const value = tagDraft.trim();
    if (!value) return;
    if (!tags.some((t) => t.toLowerCase() === value.toLowerCase())) {
      setTags((current) => [...current, value]);
    }
    setTagDraft("");
  };

  const addEntry = () => {
    if (!draftGroup) return;
    const next: DataKitItemGroupDto = {
      groupName: draftGroup,
      subgroupName: draftSubgroup || null,
    };
    if (entries.some((e) => entryKey(e) === entryKey(next))) {
      message.info("This item is already filed there");
      return;
    }
    setEntries((current) => [...current, next]);
    setDraftSubgroup(undefined);
  };

  const removeEntry = (target: DataKitItemGroupDto) => {
    setEntries((current) => current.filter((e) => entryKey(e) !== entryKey(target)));
  };

  const createGroup = () => {
    const value = newName.trim();
    if (!value || !kit.id) return;
    setCreating(true);
    DataKitApi.createGroup(kit.id, { groupName: value, name: value })
      .then(() => {
        setNewGroupOpen(false);
        setNewName("");
        setDraftGroup(value);
        setDraftSubgroup(undefined);
        onStructureChanged();
      })
      .catch(() => message.error("Could not create that group"))
      .finally(() => setCreating(false));
  };

  const createSubgroup = () => {
    const value = newName.trim();
    if (!value || !kit.id || !draftGroup) return;
    setCreating(true);
    DataKitApi.createSubgroup(kit.id, { groupName: draftGroup, name: value })
      .then(() => {
        setNewSubgroupOpen(false);
        setNewName("");
        setDraftSubgroup(value);
        onStructureChanged();
      })
      .catch(() => message.error("Could not create that subgroup"))
      .finally(() => setCreating(false));
  };

  const canSave = name.trim() !== "" && entries.length > 0;

  const save = () => {
    if (!kit.id || !canSave) return;
    const cleanedPrices = prices
      .filter((p) => (p.price || "").trim() !== "" || (p.size || "").trim() !== "")
      .map((p, i) => ({
        ...p,
        size: showSizeLabels ? p.size : "",
        sequence: i,
        overrides: p.overrides || [],
      }));

    const payload: DataKitItemDto = {
      ...item,
      name: name.trim(),
      description: description.trim(),
      extras: extras.trim() || null,
      prices: cleanedPrices,
      tags,
      groups: entries,
    };

    setSaving(true);
    const request = isNew
      ? DataKitApi.createItem(kit.id, payload)
      : DataKitApi.updateItem(item.id as string, payload);

    request
      .then(onSaved)
      .catch(() => message.error("Could not save this item"))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className="data-kit-drawer-scrim" onClick={onClose} />
      <aside className="data-kit-drawer" role="dialog" aria-label={name || "New item"}>
        <header className="data-kit-drawer-header">
          <h2>{isNew ? "New Item" : name || "Untitled"}</h2>
          <div className="data-kit-drawer-header-right">
            <span
              className="data-kit-drawer-available"
              title="Applies immediately, without publishing"
            >
              <span>Available</span>
              <Switch
                className="data-kit-available-switch"
                size="small"
                checked={available}
                disabled={!canWrite || isNew}
                onChange={(checked) => {
                  if (!kit.id || !item.id) return;
                  setAvailable(checked);
                  DataKitApi.setAvailability(kit.id, {
                    itemIds: [item.id],
                    available: checked,
                  }).catch(() => {
                    setAvailable(!checked);
                    message.error("Could not change availability");
                  });
                }}
              />
            </span>
            <button
              type="button"
              className="data-kit-drawer-close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className="data-kit-drawer-body">
          <label className="data-kit-drawer-field">
            <span>Item Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canWrite}
              maxLength={255}
              autoFocus
            />
          </label>

          <label className="data-kit-drawer-field">
            <span>Description</span>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canWrite}
              rows={3}
            />
          </label>

          <div className="data-kit-drawer-field">
            <span>Groups</span>
            {entries.length > 0 && (
              <div className="data-kit-entry-list">
                {entries.map((entry, i) => (
                  <span
                    key={entryKey(entry)}
                    className={`data-kit-entry${i === 0 ? " first" : ""}`}
                    title={i === 0 ? "Designs render this item under its first group" : undefined}
                  >
                    <span className="data-kit-entry-label">
                      {entry.groupName}
                      {entry.subgroupName && (
                        <span className="data-kit-entry-sub">{` › ${entry.subgroupName}`}</span>
                      )}
                    </span>
                    {canWrite && (
                      <button
                        type="button"
                        className="data-kit-entry-remove"
                        onClick={() => removeEntry(entry)}
                        aria-label={`Remove ${entry.groupName}`}
                      >
                        &times;
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {entries.length === 0 && (
              <span className="data-kit-drawer-error">Pick at least one group.</span>
            )}

            {canWrite && (
              <div className="data-kit-entry-add">
                <Select
                  className="data-kit-entry-select"
                  size="small"
                  placeholder="Group"
                  value={draftGroup}
                  onChange={(value) => {
                    setDraftGroup(value);
                    setDraftSubgroup(undefined);
                  }}
                  options={groups.map((g) => ({ label: g.name, value: g.name }))}
                />
                <Select
                  className="data-kit-entry-select"
                  size="small"
                  placeholder="Subgroup"
                  value={draftSubgroup}
                  onChange={setDraftSubgroup}
                  disabled={!draftGroup}
                  allowClear
                  options={subgroupsForDraft.map((s) => ({ label: s.name, value: s.name }))}
                />
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={addEntry}
                  disabled={!draftGroup}
                >
                  Add
                </Button>
              </div>
            )}

            {canWrite && (
              <div className="data-kit-entry-inline-new">
                <button
                  type="button"
                  onClick={() => {
                    setNewName("");
                    setNewGroupOpen(true);
                  }}
                >
                  + New group
                </button>
                <button
                  type="button"
                  disabled={!draftGroup}
                  title={draftGroup ? undefined : "Pick a group first"}
                  onClick={() => {
                    setNewName("");
                    setNewSubgroupOpen(true);
                  }}
                >
                  + New subgroup
                </button>
              </div>
            )}
          </div>

          <div className="data-kit-drawer-bundle">
            <div className="data-kit-drawer-field">
              <span>Price</span>
              {prices.map((price, index) => (
                <div className="data-kit-price-row" key={index}>
                  {showSizeLabels && (
                    <Input
                      className="data-kit-price-size"
                      placeholder="size"
                      value={price.size}
                      onChange={(e) => updatePrice(index, { size: e.target.value })}
                      disabled={!canWrite}
                      maxLength={255}
                    />
                  )}
                  <Input
                    className="data-kit-price-amount"
                    placeholder="price"
                    value={price.price}
                    onChange={(e) => updatePrice(index, { price: e.target.value })}
                    disabled={!canWrite}
                    maxLength={255}
                  />
                  {canWrite && prices.length > 1 && (
                    <button
                      type="button"
                      className="data-kit-price-remove"
                      onClick={() => removePrice(index)}
                      aria-label="Remove price"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              ))}
              {canWrite && (
                <div className="data-kit-price-buttons">
                  {!showSizeLabels && (
                    <Button size="small" onClick={() => setShowSizeLabels(true)}>
                      + Size Label
                    </Button>
                  )}
                  <Button size="small" onClick={addPrice}>
                    + Price
                  </Button>
                </div>
              )}
            </div>

            <label className="data-kit-drawer-field">
              <span>Extras</span>
              <Input
                placeholder="Extras"
                value={extras}
                onChange={(e) => setExtras(e.target.value)}
                disabled={!canWrite}
                maxLength={255}
              />
            </label>

            {allOverrides.length > 0 && (
              <div className="data-kit-drawer-field">
                <span>Location price overrides</span>
                <p className="data-kit-drawer-row-hint">
                  These locations charge a different price for this item.
                </p>
                {allOverrides.map(({ priceIndex, size, override }) => (
                  <div className="data-kit-override-row" key={`${priceIndex}-${override.unitId}`}>
                    <span className="data-kit-override-location">
                      {locationNameById.get(override.unitId) || `Location ${override.unitId}`}
                      {size ? ` - ${size}` : ""}
                    </span>
                    <Input
                      className="data-kit-override-price"
                      value={override.price}
                      onChange={(e) =>
                        updateOverride(priceIndex, override.unitId, { price: e.target.value })
                      }
                      disabled={!canWrite}
                      maxLength={255}
                    />
                    {canWrite && (
                      <button
                        type="button"
                        className="data-kit-price-remove"
                        onClick={() => removeOverride(priceIndex, override.unitId)}
                        aria-label="Remove override"
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="data-kit-drawer-field">
            <span>Tags</span>
            {tags.length > 0 && (
              <div className="data-kit-tag-list">
                {tags.map((tag) => (
                  <Tag
                    key={tag}
                    className="data-kit-tag"
                    closable={canWrite}
                    onClose={() => setTags((current) => current.filter((t) => t !== tag))}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}
            <Input
              placeholder="Tags"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onPressEnter={commitTag}
              onBlur={commitTag}
              disabled={!canWrite}
              maxLength={255}
            />
          </div>
        </div>

        <footer className="data-kit-drawer-footer">
          <div className="data-kit-drawer-footer-buttons">
            <Button className="data-kit-drawer-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              className="data-kit-drawer-save"
              onClick={save}
              loading={saving}
              disabled={!canWrite || !canSave}
            >
              Save
            </Button>
          </div>
          <p className="data-kit-drawer-footer-note">
            {item.usedOnCount > 0 ? (
              <>
                Used on{" "}
                <strong>
                  {item.usedOnCount} menu{item.usedOnCount === 1 ? "" : "s"}
                </strong>{" "}
                - saving reaches every design
              </>
            ) : (
              <>Not used on any design yet</>
            )}
          </p>
        </footer>
      </aside>

      <Modal
        open={newGroupOpen}
        title="New group"
        okText="Create"
        confirmLoading={creating}
        okButtonProps={{ disabled: !newName.trim() }}
        onOk={createGroup}
        onCancel={() => setNewGroupOpen(false)}
      >
        <Input
          size="large"
          placeholder="e.g. Prime Seafood"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={createGroup}
          maxLength={255}
          autoFocus
        />
      </Modal>

      <Modal
        open={newSubgroupOpen}
        title={`New subgroup in ${draftGroup || ""}`}
        okText="Create"
        confirmLoading={creating}
        okButtonProps={{ disabled: !newName.trim() }}
        onOk={createSubgroup}
        onCancel={() => setNewSubgroupOpen(false)}
      >
        <Input
          size="large"
          placeholder="e.g. Raw"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={createSubgroup}
          maxLength={255}
          autoFocus
        />
      </Modal>
    </>
  );
}
